import { useConvexMutation } from 'convex-vue';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type { useGameEngine } from './useGameEngine';

/**
 * Submits the current game run as a highscore candidate. Pass the engine
 * explicitly so this composable works in both contexts: the view that owns
 * `provideGameEngine()` (where `inject()` cannot yet see the value), and any
 * descendant that resolves it via `useInjectGameEngine()`.
 *
 * The Convex mutation upserts only when the score beats the existing record,
 * so duplicate calls for the same run (e.g. classroom watcher +
 * GameAreaHighScoreRow mount) are harmless. No-ops when the run is
 * disqualified (paused, looped, slow speed).
 */
export function useSubmitPieceHighScore(gameEngine: ReturnType<typeof useGameEngine>) {
  const submit = useConvexMutation(api.pieceHighScores.submit).mutate;

  return (pieceId: Id<'musicalPieces'>) => {
    if (!gameEngine.isHighscoreEligible.value) return;
    submit({
      musicalPieceId: pieceId,
      score: gameEngine.score.value,
      accuracy: gameEngine.accuracy.value,
      targetLineHidden: !gameEngine.targetLineVisibleDuringRun.value,
    }).catch((error) => {
      console.error('Failed to submit highscore:', error);
    });
  };
}
